import { HStack, useDisclosure, VStack } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Menu, MenuItemProps } from '@televet/kibble-ui/build/components/Menu';
import { useConversationsContext } from 'pages/Conversations/context/ConversationsContext';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FilterIconButton from 'shared/components/FilterIconButton/FilterIconButton';
import useClinicUser from 'shared/hooks/useClinicUser';
import usePrevious from 'shared/hooks/usePrevious';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';
import { IChannelListFilterSelection } from '../../../interfaces/IChannelListFilterSelection';
import ChannelListFilterPopover from '../ChannelListFilter/components/ChannelListFilterPopover';
import MarkAllAsReadConfirmationModal from './MarkAllAsReadConfirmationModal';

export enum ChannelListFilterType {
  All = 'All',
  Unread = 'Unread',
}

enum ChannelListMoreOptions {
  MarkAllAsRead = 'MarkAllAsRead',
}

const ChannelListToolbar = (): JSX.Element => {
  const {
    isSearching,
    currentChannelFilterSelection,
    isFilterApplied,
    handleSearchToggle: onSearchToggle,
    clearChannelFilter: onClearFilter,
    handleFilterSelect: onSelectFilter,
    handleChannelListViewSelect: onChannelListViewSelect,
    handleShowArchivedToggle: onShowArchivedToggle,
    filterPopoverKey,
  } = useConversationsContext();
  const { trackEvent } = useTracking();
  const { currentClinicId } = useClinicUser();
  const { isOpen: isMarkAllAsReadModalOpen, onClose: onCloseMarkAllAsReadModal, onOpen: onOpenMarkAllAsReadModal } = useDisclosure();

  const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
  const showUnreadConversationsOnly = useMemo(
    () => currentChannelFilterSelection.filters.unread,
    [currentChannelFilterSelection],
  );

  useEffect(() => {
    if (isFilterPopoverOpen) {
      trackEvent(ConversationEvents.FiltersViewed);
    }
  }, [isFilterPopoverOpen, trackEvent]);

  const handleUnreadConversationsToggle = useCallback((): void => {
    const showAllConversations = showUnreadConversationsOnly === true;
    if (showAllConversations) {
      trackEvent(ConversationEvents.FilterByAll);
    } else {
      trackEvent(ConversationEvents.FilterByUnread);
    }
    onChannelListViewSelect(showAllConversations ? ChannelListFilterType.All : ChannelListFilterType.Unread);
  }, [onChannelListViewSelect, showUnreadConversationsOnly, trackEvent]);

  const handleMoreOptionsSelect = useCallback(
    (option: MenuItemProps): void => {
      if (option.value === ChannelListMoreOptions.MarkAllAsRead) {
        trackEvent(ConversationEvents.ChannelListMarkAllAsRead);
        onOpenMarkAllAsReadModal();
      }
    },
    [onOpenMarkAllAsReadModal, trackEvent],
  );

  const handleSelectFilter = useCallback(
    (channelFilterSelection: IChannelListFilterSelection): void => {
      onSelectFilter(channelFilterSelection);
    },
    [onSelectFilter],
  );

  // Reset channel list filter popover when clinic changes
  const previousClinicId = usePrevious(currentClinicId);
  useEffect(() => {
    if (currentClinicId && previousClinicId && currentClinicId !== previousClinicId) {
      onClearFilter();
    }
  }, [currentClinicId, onClearFilter, previousClinicId]);

  return (
    <VStack spacing="0" bg="background.default" className="ChannelListToolbar__Container">
      <HStack
        borderBottom="1px"
        borderBottomColor="border.default"
        className="ChannelListToolbar__Header"
        width="100%"
        px={4}
        py={3}
        height="57px"
        alignItems="center"
      >
        <HStack className="ChannelListToolbar__Header-LeftButtons" spacing={0} gap={2} alignContent="center">
          <Button
            iconName="magnifyingGlass"
            size="md"
            variant="ghostNeutral"
            onClick={(): void => onSearchToggle(!isSearching)}
            data-testid="conversations-list-search-toggle-button"
            isActive={isSearching}
          />
          <ChannelListFilterPopover
            key={filterPopoverKey}
            trigger={
              <FilterIconButton
                data-testid="conversations-list-filter-toggle-button-container"
                buttonProps={{ 'data-testid': 'conversations-list-filter-toggle-button' }}
                showBadge={!!currentChannelFilterSelection.id || isFilterApplied}
                onFilterToggle={(): void => setIsFilterPopoverOpen((value) => !value)}
              />
            }
            isOpen={isFilterPopoverOpen}
            currentChannelFilterSelection={currentChannelFilterSelection}
            onSelectFilter={handleSelectFilter}
            onClearFilter={onClearFilter}
            onClose={(): void => setIsFilterPopoverOpen(false)}
            onShowArchivedToggle={onShowArchivedToggle}
          />
          <Button
            iconName="notification"
            variant="secondary"
            size="sm"
            borderColor="border.default"
            isActive={showUnreadConversationsOnly}
            onClick={handleUnreadConversationsToggle}
          >
            Unread
          </Button>
          <Menu
            buttonProps={{
              leftIconName: 'dotsVertical',
              showRightIcon: false,
              variant: 'ghostNeutral',
              size: 'sm',
            }}
            placement="bottom-end"
            listProps={{
              onSelect: handleMoreOptionsSelect,
              menuItems: [{ label: 'Mark all as read', value: ChannelListMoreOptions.MarkAllAsRead }],
            }}
          />
        </HStack>
      </HStack>
      <MarkAllAsReadConfirmationModal isOpen={isMarkAllAsReadModalOpen} onClose={onCloseMarkAllAsReadModal} />
    </VStack>
  );
};

export default ChannelListToolbar;
